import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Table, Badge, Alert, Form, Spinner, Row, Col } from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import type { Library } from '@googlemaps/js-api-loader';
import Swal from 'sweetalert2';
import apiDefinitions from '../../../../core/api/apiDefinitions';

interface Location {
  id?: number;
  orderId: number;
  address: string;
  lat: string;
  lng: string;
  status: string | null;
  createdAt: string | null;
}

interface User {
  empNumber: string;
  name: string;
  email: string;
  status: string | null;
  phone: string | null;
  createdBy: string | null;
  createdAt: string | null;
  group: string | null;
  departmentAdminSpecificData: any | null;
  driverSpecificData: any | null;
}

interface Vehicle {
  numberPlate: string;
  noOfPassengers: number;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface RecurringVehicleBooking {
  frequency: string;
  endDate: string | null;
}

interface Booking {
  id: number;
  vehicle: Vehicle | null;
  user: User;
  reason: string;
  intermediateApprovedBy: string | User | null;
  intermediateApprovedDate: string | null;
  comment: string | null;
  noOfPassengers: number;
  departureTime: number;
  arrivalTime: number;
  outTime: string | null;
  mileageOut: number;
  outDriverId: string | null;
  inTime: string | null;
  outConfirmedBy: string | null;
  outComment: string | null;
  mileageIn: number;
  inDriverId: string | null;
  inConfirmedBy: string | null;
  inComment: string | null;
  estimatedKms: number;
  status: string;
  driver: Driver | null;
  bookingType: string;
  priority: string;
  recurringVehicleBooking: RecurringVehicleBooking | null;
  locations: Location[];
  finalApprovedBy: string | User | null;
  finalApprovedDate: string | null;
  finalComment: string | null;
  createdBy: string;
  createdAt: string;
}

interface DetailedUser {
  empNumber: string;
  name: string;
  email?: string | null;
  status: string;
  phone?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  group: string;
  driverSpecificData?: {
    licenseNumber?: string;
    licenseType?: string;
    expireDate?: string;
  } | null;
}

interface ViewVehicleBookingsProps {
  show: boolean;
  onHide: () => void;
  booking: Booking | null;
  statusColors: { [key: string]: string };
}

interface ViewDriverModalProps {
  show: boolean;
  onHide: () => void;
  driver: Driver | null;
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0';
const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];
const center = { lat: 6.9271, lng: 79.8612 }; // Default center (Colombo, Sri Lanka)

const ViewDriverModal: React.FC<ViewDriverModalProps> = ({ show, onHide, driver }) => {
  const [details, setDetails] = useState<DetailedUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!show || !driver?.empNumber) return;
      setLoading(true);
      setErr(null);
      try {
        const res = await apiDefinitions.getUserByEmpNumber(driver.empNumber);
        if (res.data?.status === 'S1000' && res.data?.data) {
          setDetails(res.data.data as DetailedUser);
        } else {
          setErr(res.data?.statusDescription || 'Failed to load driver details');
          setDetails(null);
        }
      } catch (e: any) {
        setErr(e?.response?.data?.statusDescription || e?.message || 'Network error');
        setDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [show, driver?.empNumber]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Driver Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading...</span>
          </div>
        )}
        {!loading && err && <Alert variant="danger">{err}</Alert>}
        {!loading && !err && details && (
          <Row className="g-4">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Employee Number</Form.Label>
                <Form.Control type="text" value={details.empNumber || 'N/A'} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={details.name || 'N/A'} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={details.email || 'N/A'} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control type="text" value={details.phone || 'N/A'} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Created At</Form.Label>
                <Form.Control type="text" value={formatDate(details.createdAt)} readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Control type="text" value={details.status || 'N/A'} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>User Type</Form.Label>
                <Form.Control type="text" value={details.group || 'N/A'} readOnly />
              </Form.Group>
              {details.driverSpecificData && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>License Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={details.driverSpecificData.licenseNumber || 'N/A'}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>License Type</Form.Label>
                    <Form.Control
                      type="text"
                      value={details.driverSpecificData.licenseType || 'N/A'}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>License Expiration Date</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(details.driverSpecificData.expireDate)}
                      readOnly
                    />
                  </Form.Group>
                </>
              )}
            </Col>
          </Row>
        )}
        {!loading && !err && !details && (
          <Alert variant="warning">No driver details available.</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ViewVehicleBookings: React.FC<ViewVehicleBookingsProps> = ({
  show,
  onHide,
  booking,
  statusColors,
}) => {
  const [detailedBooking, setDetailedBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showDriverModal, setShowDriverModal] = useState<boolean>(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const formatDateTime = (timestamp: number | string | null): string => {
    if (!timestamp) return 'N/A';
    const date = new Date(typeof timestamp === 'number' ? timestamp : Date.parse(timestamp));
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const fetchBookingDetails = async (bookingId: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiDefinitions.getVehicleBookingById(bookingId);
      if (response.data.status === 'S1000' && response.data.data?.vehicleBookingRequest) {
        setDetailedBooking(response.data.data.vehicleBookingRequest);
      } else {
        setError(response.data.statusDescription || 'Failed to fetch booking details.');
        setDetailedBooking(booking); // Fallback to prop booking
        Swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: response.data.statusDescription || 'Failed to fetch booking details.',
        });
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.statusDescription || 'Failed to fetch booking details.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Error',
        text: errorMsg,
      });
      setDetailedBooking(booking); // Fallback to prop booking
    } finally {
      setLoading(false);
    }
  };

  const calculateRoute = useCallback(
    async (booking: Booking) => {
      if (!isLoaded || !booking || !booking.locations || booking.locations.length < 2) {
        return;
      }
      const locations = booking.locations.filter((loc) => loc.lat && loc.lng);
      if (locations.length < 2) {
        return;
      }
      const directionsService = new google.maps.DirectionsService();
      const waypoints = locations
        .slice(1, -1)
        .map((loc) => ({
          location: new google.maps.LatLng(parseFloat(loc.lat), parseFloat(loc.lng)),
          stopover: true,
        }));
      const origin = locations[0];
      const destination = locations[locations.length - 1];
      if (!origin || !destination || !origin.lat || !origin.lng || !destination.lat || !destination.lng) {
        return;
      }
      try {
        const result = await directionsService.route({
          origin: new google.maps.LatLng(parseFloat(origin.lat), parseFloat(origin.lng)),
          destination: new google.maps.LatLng(parseFloat(destination.lat), parseFloat(destination.lng)),
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        });
        setDirections(result);
        if (mapRef.current && result.routes[0]) {
          const bounds = new google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((point) => bounds.extend(point));
          mapRef.current.fitBounds(bounds);
        }
      } catch (error: any) {
        setError('Failed to calculate route for map.');
        Swal.fire({
          icon: 'error',
          title: 'Route Error',
          text: 'Failed to calculate route for map.',
        });
      }
    },
    [isLoaded]
  );

  useEffect(() => {
    if (show && booking?.id) {
      fetchBookingDetails(booking.id);
    } else {
      setDetailedBooking(booking);
    }
  }, [show, booking]);
  useEffect(() => {
    if (show && isLoaded && detailedBooking && detailedBooking.locations?.length >= 2) {
      calculateRoute(detailedBooking);
    }
  }, [show, isLoaded, detailedBooking, calculateRoute]);

  const renderDestinations = (locations: Location[] | undefined): JSX.Element => {
    if (!locations || locations.length === 0) {
      return <tr><td colSpan={2}>No destinations available</td></tr>;
    }
    return (
      <>
        {locations.map((location, index) => (
          <tr key={`${location.id || index}-${index}`}>
            <td>{index + 1}</td>
            <td
              title={location.address || 'N/A'}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px',
              }}
            >
              {location.address
                ? location.address.length > 40
                  ? `${location.address.slice(0, 40)}...`
                  : location.address
                : 'N/A'}
            </td>
          </tr>
        ))}
      </>
    );
  };

  if (loadError) {
    return (
      <Modal show={show} onHide={onHide} size="xl" centered autoFocus={false} enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Booking Details - ID: {booking?.id || 'N/A'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">Error loading Google Maps: {loadError.message}</Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby={`booking-${booking?.id || 'unknown'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title id={`booking-${booking?.id || 'unknown'}`}>
            Vehicle Booking Details - ID: {booking?.id || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <Alert variant="info">Loading booking details...</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && detailedBooking && (
            <div className="row">
              <div className="col-md-6">
                <h5>Booking Details</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Booking ID</strong></td>
                      <td>{detailedBooking.id}</td>
                    </tr>
                    <tr>
                      <td><strong>User</strong></td>
                      <td>
                        {detailedBooking.user?.name
                          ? `${detailedBooking.user.name.split(' ').slice(-3).join(' ')} (${detailedBooking.user.empNumber || 'N/A'})`
                          : 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Email</strong></td>
                      <td>{detailedBooking.user?.email || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Phone</strong></td>
                      <td>{detailedBooking.user?.phone || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Status</strong></td>
                      <td>
                        <Badge bg={statusColors[detailedBooking.status] || 'secondary'}>
                          {detailedBooking.status || 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Reason</strong></td>
                      <td>{detailedBooking.reason || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>No. of Passengers</strong></td>
                      <td>{detailedBooking.noOfPassengers || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Booking Type</strong></td>
                      <td>{detailedBooking.bookingType || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Priority</strong></td>
                      <td>{detailedBooking.priority || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Created By</strong></td>
                      <td>{detailedBooking.createdBy || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Created At</strong></td>
                      <td>{formatDateTime(detailedBooking.createdAt)}</td>
                    </tr>
                    <tr>
                      <td><strong>Comment</strong></td>
                      <td>{detailedBooking.comment || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="mt-3">Trip Details</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Departure Time</strong></td>
                      <td>{formatDateTime(detailedBooking.departureTime)}</td>
                    </tr>
                    <tr>
                      <td><strong>Arrival Time</strong></td>
                      <td>{formatDateTime(detailedBooking.arrivalTime)}</td>
                    </tr>
                    <tr>
                      <td><strong>Estimated KMs</strong></td>
                      <td>{detailedBooking.estimatedKms?.toFixed(2) || 'N/A'} km</td>
                    </tr>
                    <tr>
                      <td><strong>Out Time</strong></td>
                      <td>{formatDateTime(detailedBooking.outTime)}</td>
                    </tr>
                    <tr>
                      <td><strong>Mileage Out</strong></td>
                      <td>{detailedBooking.mileageOut || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>In Time</strong></td>
                      <td>{formatDateTime(detailedBooking.inTime)}</td>
                    </tr>
                    <tr>
                      <td><strong>Mileage In</strong></td>
                      <td>{detailedBooking.mileageIn || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="mt-3">Recurring Details</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Frequency</strong></td>
                      <td>{detailedBooking.recurringVehicleBooking?.frequency || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>End Date</strong></td>
                      <td>{detailedBooking.recurringVehicleBooking ? formatDateTime(detailedBooking.recurringVehicleBooking.endDate) : 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              <div className="col-md-6">
                <h5>Vehicle & Driver Details</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Vehicle</strong></td>
                      <td>
                        {detailedBooking.vehicle
                          ? `${detailedBooking.vehicle.numberPlate} (Capacity: ${detailedBooking.vehicle.noOfPassengers})`
                          : 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Driver</strong></td>
                    <td>
                      {detailedBooking.driver ? (
                        <div className="d-flex align-items-center gap-5">
                          <span>
                            {`${detailedBooking.driver.name.split(' ').slice(-3).join(' ')} (${detailedBooking.driver.empNumber})`}
                          </span>
                          <button
                            type="button"
                            className="btn btn-icon btn-sm btn-soft-secondary rounded-pill"
                            onClick={() => setShowDriverModal(true)}
                            title="View Driver Details"
                            aria-label="View Driver Details"
                          >
                            <i className="ti ti-eye" />
                          </button>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>

                    </tr>
                    <tr>
                      <td><strong>Out Driver ID</strong></td>
                      <td>{detailedBooking.outDriverId || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>In Driver ID</strong></td>
                      <td>{detailedBooking.inDriverId || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Out Confirmed By</strong></td>
                      <td>{detailedBooking.outConfirmedBy || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>In Confirmed By</strong></td>
                      <td>{detailedBooking.inConfirmedBy || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>Out Comment</strong></td>
                      <td>{detailedBooking.outComment || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td><strong>In Comment</strong></td>
                      <td>{detailedBooking.inComment || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="mt-3">Approvals</h5>
                <Table bordered hover>
                  <tbody>
                    <tr>
                      <td><strong>Intermediate Approved By</strong></td>
                      <td>
                        {typeof detailedBooking.intermediateApprovedBy === 'object' && detailedBooking.intermediateApprovedBy !== null
                          ? `${detailedBooking.intermediateApprovedBy.name
                              .split(' ')
                              .slice(-3)
                              .join(' ')} (${detailedBooking.intermediateApprovedBy.empNumber})`
                          : detailedBooking.intermediateApprovedBy || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Intermediate Approved Date</strong></td>
                      <td>{formatDateTime(detailedBooking.intermediateApprovedDate)}</td>
                    </tr>
                    <tr>
                      <td><strong>Final Approved By</strong></td>
                      <td>
                        {typeof detailedBooking.finalApprovedBy === 'object' &&
                        detailedBooking.finalApprovedBy !== null
                          ? `${detailedBooking.finalApprovedBy.name} (${detailedBooking.finalApprovedBy.empNumber})`
                          : detailedBooking.finalApprovedBy || 'N/A'}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Final Approved Date</strong></td>
                      <td>{formatDateTime(detailedBooking.finalApprovedDate)}</td>
                    </tr>
                    <tr>
                      <td><strong>Final Comment</strong></td>
                      <td>{detailedBooking.finalComment || 'N/A'}</td>
                    </tr>
                  </tbody>
                </Table>

                <h5 className="mt-3">Destinations</h5>
                <Table bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>{renderDestinations(detailedBooking.locations)}</tbody>
                </Table>

                <h5 className="mt-3">Route Map</h5>
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ height: '250px', width: '100%' }}
                    center={center}
                    zoom={12}
                    onLoad={(map) => {
                      mapRef.current = map;
                    }}
                  >
                    {detailedBooking.locations?.map((location, index) =>
                      location.lat && location.lng ? (
                        <Marker
                          key={`${location.id || index}-${index}`}
                          position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                          title={`Location ${index + 1}`}
                        />
                      ) : null
                    )}
                    {directions && (
                      <DirectionsRenderer
                        directions={directions}
                        options={{
                          polylineOptions: {
                            strokeColor: '#1976D2',
                            strokeOpacity: 0.8,
                            strokeWeight: 5,
                          },
                        }}
                      />
                    )}
                  </GoogleMap>
                ) : (
                  <Alert variant="info">Loading Google Maps...</Alert>
                )}
              </div>
            </div>
          )}
          {!loading && !error && !detailedBooking && (
            <Alert variant="warning">No booking details available.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {detailedBooking?.driver && (
        <ViewDriverModal
          show={showDriverModal}
          onHide={() => setShowDriverModal(false)}
          driver={detailedBooking.driver}
        />
      )}
    </>
  );
};

export default ViewVehicleBookings;